import compact from 'lodash/compact';
import every from 'lodash/every';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

import {
  IAutoPolicy,
  IAutoPolicyHolder,
  IAutoPolicyHolderPerson,
  IPolicyDriver,
} from '../../../interfaces';

function getDriverKey(driver: IAutoPolicyHolderPerson) {
  return compact([
    'driver',
    driver.id,
    driver.first_name,
    driver.last_name,
    driver.dob,
  ]).join('_');
}

function addAdditionalHolder(
  holderPerson: IAutoPolicyHolderPerson,
  stagedPolicy: IAutoPolicy
): IAutoPolicyHolder[] {
  const currentHolders = stagedPolicy.policy_holders;
  const pniHolder = currentHolders.find(
    ({ position_on_policy }) => position_on_policy === 1
  );

  const { id } = holderPerson;

  const idObj = id ? { id } : {};

  return [
    pniHolder!,
    {
      person: {
        ...idObj,
        first_name: holderPerson.first_name,
        last_name: holderPerson.last_name,
        dob: holderPerson.dob,
        email: pniHolder?.person.email || null,
        phone_number: pniHolder?.person.phone_number || null,
        address: stagedPolicy.rating_address,
      },
      position_on_policy: 2,
    },
  ];
}

function removeAdditionalHolder(
  stagedPolicy: IAutoPolicy
): IAutoPolicyHolder[] {
  const pniHolder = stagedPolicy.policy_holders.find(
    ({ position_on_policy }) => position_on_policy === 1
  );

  return [pniHolder!];
}

export function addNewDriverToPolicy(
  driver: IPolicyDriver,
  stagedPolicy: IAutoPolicy
): IAutoPolicy {
  const updatedPolicyHolders =
    driver.relationship_to_insured === 'spouse'
      ? addAdditionalHolder(driver.person, stagedPolicy)
      : stagedPolicy.policy_holders;

  const updatedPolicy = {
    ...stagedPolicy,
    policy_holders: updatedPolicyHolders,
    details: {
      ...stagedPolicy.details,
      drivers: [
        ...stagedPolicy.details.drivers,
        { ...driver, data_source: 'manual', is_excluded: false },
      ],
    },
  };

  return updatedPolicy;
}

export type TUpdateDriverOnPolicy = {
  driverToUpdate: IPolicyDriver;
  updatedData?: IPolicyDriver;
  isDeleting?: boolean;
  isUndoDelete?: boolean;
};
export function updateDriverOnPolicy(
  {
    driverToUpdate,
    updatedData,
    isDeleting = false,
    isUndoDelete = false,
  }: TUpdateDriverOnPolicy,
  stagedPolicy: IAutoPolicy
): {
  updatedPolicy: IAutoPolicy;
  hasNoChanges: boolean;
  isDriverSavedToPolicy: boolean;
} {
  const driverToUpdatePerson = driverToUpdate.person;
  const isDriverSavedToPolicy = !!driverToUpdate.id;
  const stagedPolicyDrivers = stagedPolicy.details.drivers;

  const updatedDrivers: IPolicyDriver[] =
    isDeleting && !isDriverSavedToPolicy
      ? stagedPolicyDrivers.filter(
          (driver) =>
            getDriverKey(driverToUpdatePerson) !== getDriverKey(driver.person)
        )
      : stagedPolicyDrivers.map((driver) => {
          const driverPerson = driver.person;
          if (
            getDriverKey(driverToUpdatePerson) === getDriverKey(driverPerson)
          ) {
            const updatedDriver: IPolicyDriver = {
              ...driver,
              ...updatedData,
              person: {
                ...driverPerson,
                ...updatedData?.person,
              },
            };

            if (isDeleting) {
              return {
                ...updatedDriver,
                deleted: true,
              };
            }
            return omit(updatedDriver, 'deleted');
          }
          return driver;
        });

  const hasNoChanges =
    !isDeleting &&
    !isUndoDelete &&
    every(
      updatedDrivers.map((driver) => {
        const oldDriver = find(stagedPolicy.details.drivers, {
          person: {
            first_name: driver?.person?.first_name,
            last_name: driver?.person?.last_name,
            dob: driver?.person?.dob,
          },
        });
        return isEqual(driver, oldDriver);
      }),
      (val) => val === true
    );
  if (hasNoChanges) {
    return { updatedPolicy: stagedPolicy, hasNoChanges, isDriverSavedToPolicy };
  }

  const wasPreviouslyASpouse =
    driverToUpdate.relationship_to_insured === 'spouse';
  const isCurrentlySetAsSpouse =
    updatedData?.relationship_to_insured === 'spouse';
  const isAddSpouseHolder = !wasPreviouslyASpouse && isCurrentlySetAsSpouse;
  const isRemoveSpouseHolder =
    (isDeleting || !isCurrentlySetAsSpouse) && wasPreviouslyASpouse;

  const updatedPolicyHolders = isRemoveSpouseHolder
    ? removeAdditionalHolder(stagedPolicy)
    : isAddSpouseHolder
    ? addAdditionalHolder(updatedData!.person, stagedPolicy)
    : stagedPolicy.policy_holders;

  const updatedPolicy = {
    ...stagedPolicy,
    policy_holders: updatedPolicyHolders,
    details: {
      ...stagedPolicy.details,
      drivers: updatedDrivers,
    },
  };
  return { updatedPolicy, hasNoChanges, isDriverSavedToPolicy };
}
